import React, { forwardRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { cn } from '@quantum-ecosystem/ui-toolkit';

const SIZE_MAP = {
	xs: { dimension: 24, fontSize: 11 },
	sm: { dimension: 32, fontSize: 13 },
	md: { dimension: 40, fontSize: 14 },
	lg: { dimension: 48, fontSize: 16 },
	xl: { dimension: 56, fontSize: 18 },
};

function getInitials(name) {
	if (!name || typeof name !== 'string') return '';
	const parts = name.trim().split(/\s+/);
	if (parts.length === 0 || !parts[0]) return '';
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const loadedImageCache = new Set();

export const FormAvatar = forwardRef(({
	src,
	image,
	alt,
	name,
	size = 'md',
	radius = 'xl',
	shape,
	color,
	style,
	className,
	children,
	...restProps
}, ref) => {
	const effectiveSrc = (src || image || null);
	const effectiveName = name || (alt && !alt.toLowerCase().includes('profile') && !alt.toLowerCase().includes('avatar') ? alt : '');
	const initials = getInitials(effectiveName);

	// Map radius to shape
	const resolvedShape = shape || (radius === 'sm' || radius === 'md' ? 'rounded' : 'circle');

	// Determine dimensions & font size
	let dimension = 40;
	let fontSize = 14;
	let customWidth;
	let customHeight;

	if (typeof size === 'number') {
		dimension = size;
		fontSize = Math.max(10, Math.round(dimension * 0.38));
	} else if (SIZE_MAP[size]) {
		dimension = SIZE_MAP[size].dimension;
		fontSize = SIZE_MAP[size].fontSize;
	} else if (typeof size === 'string') {
		customWidth = size;
		customHeight = size;
		const parsed = parseInt(size, 10);
		if (!isNaN(parsed)) {
			fontSize = Math.max(10, Math.round(parsed * 0.38));
		}
	}

	const [isLoaded, setIsLoaded] = useState(() => {
		if (!effectiveSrc) return false;
		return loadedImageCache.has(effectiveSrc);
	});
	const [hasError, setHasError] = useState(false);

	useEffect(() => {
		if (!effectiveSrc) {
			setIsLoaded(false);
			setHasError(false);
			return;
		}

		if (loadedImageCache.has(effectiveSrc)) {
			setIsLoaded(true);
			setHasError(false);
			return;
		}

		setIsLoaded(false);
		setHasError(false);

		const img = new Image();
		img.src = effectiveSrc;
		if (img.complete && img.naturalWidth > 0) {
			loadedImageCache.add(effectiveSrc);
			setIsLoaded(true);
		}
	}, [effectiveSrc]);

	const isLoading = Boolean(effectiveSrc && !isLoaded && !hasError);

	const containerStyle = {
		position: 'relative',
		width: customWidth || `${dimension}px`,
		height: customHeight || `${dimension}px`,
		minWidth: customWidth || `${dimension}px`,
		minHeight: customHeight || `${dimension}px`,
		fontSize: `${fontSize}px`,
		backgroundColor: color || 'var(--q-gray-500, #64748b)',
		color: '#ffffff',
		overflow: 'hidden',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		userSelect: 'none',
		fontWeight: 600,
		boxSizing: 'border-box',
		...style,
	};

	return (
		<div
			ref={ref}
			className={cn(
				'q-avatar',
				resolvedShape === 'rounded' ? 'q-avatar-rounded' : 'q-avatar-circle',
				className
			)}
			style={containerStyle}
			title={effectiveName || alt || undefined}
			{...restProps}
		>
			{/* Base Layer: Children, Initials, or Silhouette Icon (animated during loading, not flat full grey) */}
			<div
				className={isLoading ? 'q-avatar-loading' : undefined}
				style={{
					position: 'absolute',
					inset: 0,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					width: '100%',
					height: '100%',
				}}
			>
				{children ? (
					<span style={{ lineHeight: 1 }}>{children}</span>
				) : initials ? (
					<span style={{ lineHeight: 1 }}>{initials}</span>
				) : (
					<span
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: '100%',
							height: '100%',
							opacity: 0.7,
						}}
					>
						<svg width="60%" height="60%" viewBox="0 0 24 24" fill="currentColor">
							<path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
						</svg>
					</span>
				)}
			</div>

			{/* Top Layer: Smooth Fade-in Image */}
			{effectiveSrc && !hasError && (
				<img
					src={effectiveSrc}
					alt={alt || effectiveName || 'Avatar'}
					onLoad={() => {
						if (effectiveSrc) loadedImageCache.add(effectiveSrc);
						setIsLoaded(true);
					}}
					onError={() => setHasError(true)}
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						objectFit: 'cover',
						opacity: isLoaded ? 1 : 0,
						transition: 'opacity 0.25s ease-in-out',
					}}
				/>
			)}
		</div>
	);
});

FormAvatar.displayName = 'FormAvatar';

FormAvatar.propTypes = {
	src: PropTypes.string,
	image: PropTypes.string,
	alt: PropTypes.string,
	name: PropTypes.string,
	size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	shape: PropTypes.oneOf(['circle', 'rounded']),
	color: PropTypes.string,
	style: PropTypes.object,
	className: PropTypes.string,
	children: PropTypes.node,
};

export default FormAvatar;
